import type { ThemePreference } from '../lib/theme';
import type { PayCycle } from '../lib/payPeriod';

/** Bumped whenever the persisted shape changes; storage layer migrates. */
export const DATA_VERSION = 9;

export type BudgetModel = 'daily' | '503020' | 'pyf' | 'zero' | 'conscious';

export type Bucket503020 = 'needs' | 'wants' | 'savings';

export type TransactionType = 'expense' | 'income';

export type ScheduledFrequency =
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'yearly'
  | 'one_time';

export type TransactionSource = 'manual' | 'plaid';

export interface Profile {
  name: string;
  budgetModel: BudgetModel;
  payCycle: PayCycle;
  /** Cycle-dependent: weekly = day-of-week (0..6); monthly/semimonthly = day-of-month (1..31). */
  payDay: number;
  /** Semimonthly only — second pay day of month (1..31). */
  payDay2?: number;
  /** Biweekly only — ISO date (YYYY-MM-DD) of any past pay day; anchors the 14-day cycle. */
  payAnchor?: string;
  currency: string;
  /** Pay Yourself First model — hourly take-home. */
  hourlyRate?: number;
  /**
   * Pay Yourself First model — number of workdays per week. Drives
   * pyfPeriodReserve and the weekly/monthly/yearly projections.
   * Defaults to 5 (Mon–Fri). Allowed values: 3, 4, 5, 6, 7.
   */
  workDaysPerWeek?: number;
  /**
   * Legacy. Kept as optional metadata only — never read by budget math.
   * Income is sourced from Plan-tab transactions + scheduled income items.
   * v2 → v3 migration moves any positive value into a recurring scheduled
   * income so existing users keep working without manual re-entry.
   */
  monthlyIncome?: number;
  /**
   * Today-screen hero card view preference. 'daily' shows the per-day
   * allowance (period income / days remaining); 'period' shows total
   * remaining for the period. When undefined, TodayScreen derives a
   * smart default based on the user's income shape (lump-sum users
   * prefer 'period', spread-paycheck users prefer 'daily').
   */
  heroView?: 'daily' | 'period';
  /**
   * Rollover behavior for unspent budget at the end of a period.
   *   - 'continuous': running surplus/deficit from all prior periods
   *     is folded into the current period's pool — savings momentum.
   *   - 'fresh': each period resets; only this period's income counts.
   * Defaults to 'continuous' for new users (per the v3 → v4 migration).
   */
  rollover?: 'continuous' | 'fresh';
  onboardingComplete: boolean;
  walkthroughComplete: boolean;
  /** One-tap shortcuts for the Today screen. Capped at 8. */
  quickPresets: QuickPreset[];
  /**
   * Rolling daily net-worth snapshots. Grown by one entry per UTC
   * day on cold-start; capped at 365 entries (~1 year of history).
   * Optional for backward-compat with v7 blobs; v7→v8 migration backfills [].
   */
  netWorthHistory?: NetWorthSnapshot[];
  /**
   * Achievement subsystem state. v8→v9 migration backfills with empty
   * lists + an `installedAt` timestamp. Optional for back-compat.
   */
  achievements?: AchievementsState;
}

export interface AchievementUnlock {
  id: string;
  unlockedAt: string;
  /** False until the user dismisses the toast or visits the screen. */
  seen: boolean;
}

export interface AchievementProgress {
  id: string;
  progress: number;
  updatedAt: string;
}

export interface AchievementStreakBucket {
  current: number;
  best: number;
  /** ISO YYYY-MM-DD of the last day this streak ticked. */
  lastDate: string | null;
}

export interface AchievementsState {
  unlocks: AchievementUnlock[];
  progress: AchievementProgress[];
  streaks: {
    daysLogged: AchievementStreakBucket;
    daysUnderPace: AchievementStreakBucket;
    noSpendDays: AchievementStreakBucket;
    pyfStreak: AchievementStreakBucket;
  };
  /** Set the first time the app boots a fresh AppData. */
  installedAt: string;
  /** Master toggle in Settings → Achievements. */
  achievementsEnabled: boolean;
}

export interface NetWorthSnapshot {
  /** ISO YYYY-MM-DD when the snapshot was taken. */
  date: string;
  total: number;
  breakdown: {
    checking: number;
    savings: number;
    investments: number;
    creditCards: number;
    loans: number;
    other: number;
  };
}

/**
 * One-tap transaction shortcut surfaced on the Today screen above the
 * Quick Add buttons. Free feature; capped at 8 entries (the cap is
 * enforced by the store action).
 */
export interface QuickPreset {
  id: string;
  /** Display label, max 24 chars enforced by the editor. */
  label: string;
  amount: number;
  categoryId: string;
  transactionType: TransactionType;
  /** ISO timestamp set when the preset is created. */
  createdAt: string;
  /** Render order — lower comes first. Reassigned by reorderQuickPresets. */
  sortOrder: number;
}

export interface Category {
  id: string;
  name: string;
  /** Lucide icon name (string so it serializes; UI resolves to component). */
  icon: string;
  type: TransactionType;
  /** Hex color, e.g. "#FF9500". */
  color: string;
  /** Sort order — lower comes first. */
  order: number;
  /** Tag for the 50/30/20 model (only meaningful for expense categories). */
  bucket503020?: Bucket503020;
  /** Monthly allocation for the Zero-Based model (expense categories only). */
  zeroBasedBudget?: number;
}

export interface Transaction {
  id: string;
  amount: number;
  type: TransactionType;
  categoryId: string;
  description: string;
  /** Calendar date the transaction belongs to: "YYYY-MM-DD". */
  date: string;
  /** Full ISO timestamp of when the row was created. */
  createdAt: string;
  source: TransactionSource;
  /** Plaid's stable id, used for de-duping syncs. */
  plaidTransactionId?: string;
  /** Marks rows materialized from a ScheduledItem. */
  recurring?: boolean;
  /**
   * If set, this transaction was auto-created from the ScheduledItem
   * with this id by `processScheduledBills`. Used to dedupe so repeated
   * loads / Today-tab mounts don't spawn duplicate rows for the same
   * due date.
   */
  generatedFromScheduledId?: string;
  /**
   * Optional amortization window. When > 1, budget calculators treat
   * the transaction as `amount / spreadDays` per day for `spreadDays`
   * consecutive days starting from `date`. A $500 purchase with
   * spreadDays=45 reads as $11.11/day for 45 days. Undefined / 1 = no
   * spread (full amount lands on `date`).
   */
  spreadDays?: number;
  /**
   * Optional explicit end date for the spread (ISO YYYY-MM-DD). Set when
   * the user marks a spread paid off (today's date) or makes a partial
   * payment that shifts the natural end earlier. The contribution math
   * uses `min(naturalEnd, spreadEndedOn)` so any earlier end forgives
   * the remaining future contribution from the budget.
   */
  spreadEndedOn?: string;
  /**
   * IndexedDB receipt id when a captured photo is attached. Receipt
   * blob lives outside AppData (in the dedicated `margin-receipts`
   * idb store) so transactions stay lightweight to serialize. Optional
   * additive field — no migration needed.
   */
  receiptId?: string;
}

export interface ScheduledItem {
  id: string;
  name: string;
  amount: number;
  type: TransactionType;
  categoryId: string;
  frequency: ScheduledFrequency;
  /** Next due date in "YYYY-MM-DD" form. */
  nextDate: string;
  active: boolean;
}

export interface Goal {
  id: string;
  name: string;
  targetAmount: number;
  currentAmount: number;
  /** Optional target date in "YYYY-MM-DD" form. */
  deadline?: string;
  /** Lucide icon name. */
  icon: string;
}

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  /** ISO timestamp when unlocked; undefined while still locked. */
  unlockedAt?: string;
}

export interface ProTrialState {
  /** ISO timestamp when the 7-day trial began. */
  started: string;
}

export type AccountType = 'checking' | 'savings' | 'credit';

/**
 * Manual account balance — visibility layer only. Users type the
 * current balance themselves; budget calculations never read this.
 * Surfaced on the Today tab via CashPositionCard.
 */
export interface Account {
  id: string;
  name: string;
  type: AccountType;
  balance: number;
  /** ISO date the user last updated this balance. */
  updatedAt: string;
  /** Reserved for future Plaid linking — not consumed by manual flow. */
  plaidAccountId?: string;
}

export interface AppData {
  version: number;
  profile: Profile;
  categories: Category[];
  transactions: Transaction[];
  scheduledItems: ScheduledItem[];
  goals: Goal[];
  achievements: Achievement[];
  /** ISO timestamp of the first install. Used by the "Consistent" achievement. */
  installedAt?: string;
  /** Tool ids that have been opened at least once — used by "Tool Time". */
  toolsUsed?: string[];
  theme: ThemePreference;
  /** Accent palette key — see src/tokens.ts `accentPalette`. */
  accentColor: string;
  rated: boolean;
  rateSnoozed?: string;
  rateNever?: boolean;
  proTrial?: ProTrialState;
  proPurchased?: boolean;
  /** Manual account balances (visibility layer; not used by budget math). */
  accounts: Account[];
}

/** Input shape for adding a Transaction — id and createdAt are filled by the store. */
export type NewTransactionInput = Omit<Transaction, 'id' | 'createdAt'>;

/** Input shape for adding a Category — id and order are filled by the store. */
export type NewCategoryInput = Omit<Category, 'id' | 'order'>;

/** Input shape for adding a ScheduledItem — id is filled by the store. */
export type NewScheduledItemInput = Omit<ScheduledItem, 'id'>;

/** Input shape for adding a Goal — id is filled by the store. */
export type NewGoalInput = Omit<Goal, 'id' | 'currentAmount'> & {
  currentAmount?: number;
};

/** Input shape for adding an Account — id and updatedAt are filled by the store. */
export type NewAccountInput = Omit<Account, 'id' | 'updatedAt'>;

/** Input shape for adding a QuickPreset — id, createdAt, sortOrder filled by the store. */
export type NewQuickPresetInput = Omit<
  QuickPreset,
  'id' | 'createdAt' | 'sortOrder'
>;
